import type { FC } from 'react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography } from '@mui/material';

import type { DetailedFollowUpTicket } from 'src/types/followUp';
import type { SurveyDetail } from 'src/types/survey';
import BaseSnackbar from 'src/components/common/Snackbar';

import FeedbackDownloadButton from './FeedbackDownloadButton';
import NpsScoreBox from './NpsScoreBox';

import { styles } from 'src/components/followUp/TicketOverviewPage/TicketDetail/ticketDetail.styles';

const FeedbackSection: FC<{
  ticket: DetailedFollowUpTicket;
  survey?: SurveyDetail;
}> = ({ ticket, survey }) => {
  const { t } = useTranslation(['common', 'followUp']);
  const [showComingSoonSnackbar, setShowComingSoonSnackbar] = useState(false);
  if (!ticket) return null;

  const header = t('followUp:feedback');

  interface OpenCommentsWithHeadlinesProps {
    headline: string;
    text: string;
  }
  const openCommentsWithHeadlines = Array.isArray(
    ticket.openCommentsWithHeadlines,
  )
    ? [
        ...(ticket.openCommentsWithHeadlines as OpenCommentsWithHeadlinesProps[]),
      ]
    : [];

  const feedbackField2: string[] | null = ticket.feedbackField2
    ? ticket.feedbackField2.split('\n')
    : null;

  return (
    <Box
      component="section"
      aria-label={header}
      sx={[{ gap: 3 }, styles.modalSection]}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography component="h5">{header}</Typography>
        <FeedbackDownloadButton ticket={ticket} />
      </Box>

      <Box sx={styles.feedbackGrid}>
        <Box sx={styles.feedbackDataRow}>
          <NpsScoreBox ticket={ticket} />
          <Typography>
            {t('followUp:npsGroupLabel', {
              nps: t(`followUp:npsGroup.${ticket.npsGroup}`),
            })}
          </Typography>
        </Box>
      </Box>

      {survey?.feedbackField2Label && feedbackField2 && (
        <>
          <Divider />

          <Typography>{survey.feedbackField2Label}</Typography>

          <ol style={{ paddingLeft: 20 }}>
            {feedbackField2.map((feedback) => (
              <li key={feedback}>
                <Typography>{feedback}</Typography>
              </li>
            ))}
          </ol>
        </>
      )}

      {openCommentsWithHeadlines.length ? (
        <>
          <Divider />
          {openCommentsWithHeadlines.map((item, i) =>
            item.text ? (
              <Fragment key={i}>
                {item.headline && <Typography>{item.headline}</Typography>}
                <Typography
                  sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}
                >
                  {`“${item.text}”`}
                </Typography>
              </Fragment>
            ) : null,
          )}
        </>
      ) : null}

      <BaseSnackbar
        open={showComingSoonSnackbar}
        onClose={() => setShowComingSoonSnackbar(false)}
        message={t('common:comingSoon')}
      />
    </Box>
  );
};

export default FeedbackSection;
